import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { formatOneworld } from "@app/components/oneworld-word";
import {
  EVENT_CONTRACT_CODE,
  GET_LIVE_CONTRACT,
  PREVIEW_EVENT_AGREEMENT,
} from "@app/graphql/events.queries";
import {
  type AgreementSubmissionPreview,
  useAgreementSubmission,
} from "@app/hooks/use-agreement-submission";
import {
  parseContractMetadataSchema,
  validateContractMetadataValues,
} from "@app/lib/contract-metadata";
import {
  decodeEventDescription,
  encodeEventDescription,
} from "@app/lib/event-description";
import {
  type PendingEventFormValues,
  loadPendingEvent,
} from "@app/lib/pending-event-storage";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import ReactMarkdown from "react-markdown";
import { Navigate, useLocation, useNavigate } from "react-router";
import "./agreement-review-page.css";

type LiveContract = {
  contractId: string;
  content: string;
  metadataSchema: unknown;
};

const DEFAULT_COMPANY_NAME = "Default Company";

function normalizeOrganizerName(value: string | null | undefined) {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function parsePreviewValues(value: unknown): Record<string, string> | null {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed) as unknown;
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  return Object.values(parsed).every((entry) => typeof entry === "string")
    ? (parsed as Record<string, string>)
    : null;
}

function AgreementContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => (
          <h1 className="mb-8 text-2xl font-bold leading-tight text-[#181818] sm:text-[32px]">
            {formatOneworld(children)}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="mb-3 mt-8 text-base font-bold text-[#181818]">
            {formatOneworld(children)}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="mb-3 mt-8 text-[15px] font-bold text-[#181818]">
            {formatOneworld(children)}
          </h3>
        ),
        p: ({ children }) => {
          const parts = Children.toArray(children);
          const description = parts[1];
          if (
            parts.length === 2 &&
            isValidElement<{ children?: ReactNode }>(parts[0]) &&
            parts[0].type === "strong" &&
            parts[0].props.children === "Event Description:" &&
            typeof description === "string" &&
            description.trim().startsWith("b64:")
          ) {
            return (
              <p className="mb-4 whitespace-pre-line text-[15px] leading-[1.75] text-[#444]">
                {parts[0]} {decodeEventDescription(description.trim())}
              </p>
            );
          }

          return (
            <p className="mb-4 text-[15px] leading-[1.75] text-[#444]">
              {formatOneworld(children)}
            </p>
          );
        },
        ul: ({ children }) => (
          <ul className="mb-4 list-disc space-y-2 pl-6 text-[15px] leading-[1.75] text-[#444]">
            {children}
          </ul>
        ),
        ol: ({ children }) => (
          <ol className="mb-4 list-decimal space-y-2 pl-6 text-[15px] leading-[1.75] text-[#444]">
            {children}
          </ol>
        ),
        a: ({ children, href }) => (
          <a
            className="font-semibold text-[#120c80] underline underline-offset-2"
            href={href}
            rel="noreferrer noopener"
            target="_blank"
          >
            {formatOneworld(children)}
          </a>
        ),
        li: ({ children }) => <li>{formatOneworld(children)}</li>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function useFormValuesFromState(): PendingEventFormValues | null {
  const location = useLocation();
  return useMemo(() => {
    const fromNav = (
      location.state as { formValues?: PendingEventFormValues } | null
    )?.formValues;
    if (fromNav) return fromNav;
    return loadPendingEvent();
  }, [location.state]);
}

export function AgreementReviewPage() {
  const navigate = useNavigate();
  const { user } = useJwtAuth();
  const formValues = useFormValuesFromState();
  const {
    data: contractData,
    loading: isContractLoading,
    error: contractError,
    refetch: refetchContract,
  } = useQuery<{ getLiveContract: LiveContract }, { code: string }>(
    GET_LIVE_CONTRACT,
    {
      variables: { code: EVENT_CONTRACT_CODE },
      fetchPolicy: "cache-first",
    },
  );
  const contract = contractData?.getLiveContract;

  const [signatureName, setSignatureName] = useState(
    formValues?.agreementWorkflow?.typedSignature ?? "",
  );
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAcknowledgedAgreement, setHasAcknowledgedAgreement] =
    useState(false);
  const [metadataValues, setMetadataValues] = useState<Record<string, string>>(
    {},
  );
  const [metadataContractId, setMetadataContractId] = useState<string | null>(
    null,
  );
  const [agreementPreview, setAgreementPreview] =
    useState<AgreementSubmissionPreview | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const agreementContentRef = useRef<HTMLDivElement>(null);
  const activeContractIdRef = useRef(contract?.contractId ?? null);
  activeContractIdRef.current = contract?.contractId ?? null;

  const metadataConfiguration = useMemo(() => {
    if (!contract) return { fields: [], error: null };
    try {
      return {
        fields: parseContractMetadataSchema(contract.metadataSchema),
        error: null,
      };
    } catch (error) {
      return {
        fields: [],
        error:
          error instanceof Error
            ? error.message
            : "The agreement metadata configuration is invalid.",
      };
    }
  }, [contract]);
  const previewMetadataFields = useMemo(
    () => metadataConfiguration.fields.filter((field) => !field.reserved),
    [metadataConfiguration.fields],
  );
  const serializedMetadataValues = JSON.stringify(metadataValues);
  const serializedPreviewValues = JSON.stringify({
    ...metadataValues,
    ...(metadataValues.description !== undefined
      ? { description: encodeEventDescription(metadataValues.description) }
      : {}),
  });
  const metadataValidationError = validateContractMetadataValues(
    previewMetadataFields,
    metadataValues,
  );
  const requiresPreview = Boolean(contract);
  const isPreviewCurrent =
    agreementPreview?.contractId === contract?.contractId &&
    agreementPreview?.sourceValues === serializedMetadataValues;
  const previewContent = isPreviewCurrent ? agreementPreview.content : null;

  useEffect(() => {
    if (!contract || metadataConfiguration.error) return;
    const storedWorkflow = formValues?.agreementWorkflow;
    const formMetadataValues: Record<string, string | undefined> = {
      companyName: DEFAULT_COMPANY_NAME,
      venueName: formValues?.venueName,
      venueAddress: formValues?.venueAddress,
      eventName: formValues?.eventName,
      description: formValues?.description,
      eventStartDate: formValues?.departDate,
      eventEndDate: formValues?.returnDate,
      destination: formValues?.destinationLabel,
      estimatedTravelerCount: formValues?.passengers,
    };
    setMetadataValues(
      Object.fromEntries(
        previewMetadataFields.map((field) => [
          field.key,
          storedWorkflow?.templateContractId === contract.contractId
            ? field.key === "description"
              ? decodeEventDescription(storedWorkflow.values[field.key] ?? "")
              : (storedWorkflow.values[field.key] ?? "")
            : (formMetadataValues[field.key] ?? ""),
        ]),
      ),
    );
    setAgreementPreview(null);
    setMetadataContractId(contract.contractId);
  }, [
    contract,
    previewMetadataFields,
    formValues,
    metadataConfiguration.error,
  ]);

  const [previewEventAgreementMutation] = useMutation<{
    previewEventAgreement: {
      contractId: string;
      content: string;
      values: unknown;
      acceptedAt: string;
      renderVersion: string;
      previewHash: string;
    };
  }>(PREVIEW_EVENT_AGREEMENT);
  const resetAgreementReview = useCallback(() => {
    setHasScrolledToBottom(false);
    setHasAcknowledgedAgreement(false);
    if (agreementContentRef.current) {
      agreementContentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    resetAgreementReview();
  }, [contract?.contractId, resetAgreementReview]);

  useEffect(() => {
    const agreementContent = agreementContentRef.current;
    if (
      !previewContent ||
      !agreementContent ||
      agreementContent.clientHeight === 0 ||
      agreementContent.scrollHeight > agreementContent.clientHeight + 1
    ) {
      return;
    }

    setHasScrolledToBottom(true);
  }, [previewContent]);

  const handleAgreementScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!previewContent) return;

      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      if (scrollTop > 0 && scrollHeight - scrollTop <= clientHeight + 1) {
        setHasScrolledToBottom(true);
      }
    },
    [previewContent],
  );

  const hasSignature = signatureName.trim().length > 0;
  const doesSignatureMatchUser =
    hasSignature &&
    Boolean(user?.name) &&
    normalizeOrganizerName(signatureName) ===
      normalizeOrganizerName(user?.name);
  const canSign =
    Boolean(contract) &&
    Boolean(user?.email) &&
    hasScrolledToBottom &&
    hasAcknowledgedAgreement &&
    doesSignatureMatchUser &&
    !metadataConfiguration.error &&
    !metadataValidationError &&
    isPreviewCurrent;
  const { submissionPhase, submitAgreement } = useAgreementSubmission({
    formValues,
    contractId: contract?.contractId ?? null,
    agreementPreview,
    signatureName,
    canSubmit: canSign,
  });
  const isSubmitting = submissionPhase !== "idle";

  const handlePreview = useCallback(async () => {
    if (!contract || metadataConfiguration.error || metadataValidationError)
      return;
    resetAgreementReview();
    setIsPreviewing(true);
    setPreviewError(null);
    setAgreementPreview(null);
    try {
      const { data } = await previewEventAgreementMutation({
        variables: {
          contractId: contract.contractId,
          values: serializedPreviewValues,
        },
      });
      const preview = data?.previewEventAgreement;
      const previewValues = parsePreviewValues(preview?.values);
      if (
        !preview?.content ||
        preview.contractId !== contract.contractId ||
        !previewValues ||
        !preview.acceptedAt ||
        previewValues.acceptedAt !== preview.acceptedAt ||
        !preview.renderVersion ||
        !/^[a-f0-9]{64}$/.test(preview.previewHash)
      ) {
        throw new Error("Agreement preview is unavailable.");
      }
      if (activeContractIdRef.current !== contract.contractId) return;
      setAgreementPreview({
        contractId: contract.contractId,
        content: preview.content,
        values: Object.fromEntries(
          Object.entries(previewValues).filter(
            ([key]) => key !== "signerName" && key !== "acceptedAt",
          ),
        ),
        sourceValues: serializedMetadataValues,
        acceptedAt: preview.acceptedAt,
        renderVersion: preview.renderVersion,
        previewHash: preview.previewHash,
      });
    } catch {
      if (activeContractIdRef.current !== contract.contractId) return;
      setPreviewError(
        "The agreement could not be previewed. Check the details and try again.",
      );
    } finally {
      if (activeContractIdRef.current === contract.contractId) {
        setIsPreviewing(false);
      }
    }
  }, [
    contract,
    metadataConfiguration.error,
    metadataValidationError,
    previewEventAgreementMutation,
    resetAgreementReview,
    serializedMetadataValues,
    serializedPreviewValues,
  ]);

  const automaticallyPreviewedContractId = useRef<string | null>(null);
  useEffect(() => {
    if (
      !contract ||
      metadataContractId !== contract.contractId ||
      !requiresPreview ||
      metadataConfiguration.error ||
      metadataValidationError ||
      automaticallyPreviewedContractId.current === contract.contractId
    ) {
      return;
    }

    automaticallyPreviewedContractId.current = contract.contractId;
    void handlePreview();
  }, [
    contract,
    handlePreview,
    metadataConfiguration.error,
    metadataContractId,
    metadataValidationError,
    requiresPreview,
  ]);

  if (!formValues) {
    return <Navigate to="/" replace />;
  }

  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="agreement-review-page fixed inset-0 z-50 flex flex-col bg-white font-display">
      {/* Header */}
      <div className="agreement-review-page__header flex items-center justify-between px-4 py-4 border-b border-gray-100 bg-white flex-shrink-0 sm:px-8">
        <button
          type="button"
          onClick={() => navigate("/")}
          disabled={isSubmitting}
          className="flex items-center gap-2 text-[#120c80] hover:opacity-70 transition-opacity font-medium text-[15px] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Back to details
        </button>

        <h2 className="absolute left-1/2 -translate-x-1/2 text-lg font-bold text-[#120c80] hidden sm:block">
          Review agreement
        </h2>

        <div className="flex items-center gap-4">
          <span className="hidden text-[13px] font-semibold text-gray-400 uppercase tracking-wider md:block">
            Step 2 of 2
          </span>
        </div>
      </div>

      {/* Mobile title (shown below header on small screens) */}
      <div className="agreement-review-page__mobile-title px-4 py-3 border-b border-gray-100 bg-white sm:hidden">
        <h2 className="text-lg font-bold text-[#120c80] text-center">
          Review agreement
        </h2>
      </div>

      {/* Scrollable Agreement Body */}
      <div
        ref={agreementContentRef}
        role="region"
        aria-label="Agreement content"
        onScroll={handleAgreementScroll}
        className="agreement-review-page__content flex-1 overflow-y-auto relative"
      >
        <div className="agreement-review-page__document max-w-[760px] mx-auto px-4 py-4 sm:px-6 sm:py-12">
          <div className="agreement-review-page__intro mb-6 sm:mb-10">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#120c80]">
              Event Services Agreement
            </p>
            <div className="mt-4 h-px bg-gray-100 sm:mt-6" />
          </div>

          {isContractLoading && (
            <div
              aria-live="polite"
              className="py-12 text-center text-sm text-gray-500"
            >
              Loading agreement...
            </div>
          )}

          {!isContractLoading && (contractError || !contract) && (
            <div
              className="border-y border-red-200 py-8 text-center"
              role="alert"
            >
              <p className="font-semibold text-[#181818]">
                Unable to load the agreement
              </p>
              <p className="mt-2 text-sm text-gray-600">
                The current agreement must be available before you can sign.
              </p>
              <button
                type="button"
                onClick={() => void refetchContract()}
                className="mt-5 font-semibold text-[#120c80] underline underline-offset-4"
              >
                Try again
              </button>
            </div>
          )}

          {contract && metadataConfiguration.error && (
            <div className="border-y border-red-200 py-6" role="alert">
              <p className="font-semibold text-[#181818]">
                Agreement configuration error
              </p>
              <p className="mt-2 text-sm text-gray-600">
                {metadataConfiguration.error}
              </p>
            </div>
          )}

          {contract &&
            !metadataConfiguration.error &&
            (!requiresPreview || previewContent) && (
              <AgreementContent content={previewContent ?? contract.content} />
            )}

          {contract &&
            !metadataConfiguration.error &&
            requiresPreview &&
            !previewContent &&
            !previewError &&
            isPreviewing &&
            !metadataValidationError && (
              <div
                aria-live="polite"
                className="border-y border-gray-200 py-12 text-center text-sm text-gray-500"
              >
                Generating agreement preview...
              </div>
            )}

          {contract && !metadataConfiguration.error && previewError && (
            <div
              className="border-y border-red-200 py-8 text-center"
              role="alert"
            >
              <p className="text-sm text-red-600">{previewError}</p>
              <button
                type="button"
                onClick={() => void handlePreview()}
                className="mt-5 font-semibold text-[#120c80] underline underline-offset-4"
              >
                Try again
              </button>
            </div>
          )}

          {contract && <div className="h-10" />}
        </div>
      </div>

      {/* Sticky Sign Footer */}
      <div className="agreement-review-page__footer border-t border-gray-200 bg-[#fcfcfd] flex-shrink-0">
        <div className="agreement-review-page__footer-content max-w-[760px] mx-auto px-4 py-3 sm:px-6 sm:py-6">
          {!hasScrolledToBottom ? (
            <div className="agreement-review-page__review-cue-lane">
              <span className="agreement-review-page__review-cue">
                Scroll to review full agreement
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            </div>
          ) : null}
          <div
            className={`agreement-review-page__signing-form${
              !hasScrolledToBottom
                ? " agreement-review-page__signing-form--with-review-cue"
                : ""
            }`}
          >
            <p className="agreement-review-page__sign-heading mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400 sm:mb-4">
              Sign below to accept
            </p>

            <label className="sr-only" htmlFor="agreement-signature">
              Full legal name
            </label>
            <input
              id="agreement-signature"
              type="text"
              placeholder="Type your full legal name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              disabled={!contract || isSubmitting}
              className="agreement-review-page__signature-input w-full border-b-2 border-gray-200 focus:border-[#120c80] outline-none py-1.5 text-lg text-[#252525] transition-colors bg-transparent placeholder:text-gray-300"
            />

            {hasSignature ? (
              <div
                className={`agreement-review-page__signature-preview mt-3 pb-1 border-b border-dashed border-gray-200${
                  !hasScrolledToBottom
                    ? " agreement-review-page__signature-preview--before-review"
                    : ""
                }`}
              >
                <p
                  style={{
                    fontFamily: "'Dancing Script', cursive",
                    fontSize: "30px",
                    color: "#120c80",
                    lineHeight: 1.3,
                  }}
                >
                  {signatureName}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {today} &nbsp;&middot;&nbsp; Acceptance time recorded when you
                  sign
                </p>
                {user?.name && !doesSignatureMatchUser && (
                  <p className="text-[13px] text-red-600 mt-3" role="alert">
                    The signature must match the name on your logged-in account.
                  </p>
                )}
              </div>
            ) : (
              <p className="agreement-review-page__signature-helper mt-3 hidden text-[13px] text-gray-400 sm:block">
                Type your full legal name above to sign.
              </p>
            )}

            <label
              className={`agreement-review-page__acknowledgment mt-3 flex items-start gap-2 text-sm sm:mt-5 sm:gap-3 ${
                contract && hasScrolledToBottom && !isSubmitting
                  ? "cursor-pointer text-[#252525]"
                  : "cursor-not-allowed text-gray-400"
              }`}
            >
              <input
                type="checkbox"
                checked={hasAcknowledgedAgreement}
                onChange={(event) =>
                  setHasAcknowledgedAgreement(event.target.checked)
                }
                disabled={!contract || !hasScrolledToBottom || isSubmitting}
                className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer accent-[#120c80] disabled:cursor-not-allowed"
              />
              <span className="font-semibold">I have read and agree</span>
            </label>

            <div className="agreement-review-page__legal-copy mb-3 mt-2 flex items-center gap-1.5 text-xs text-gray-400 sm:mb-5 sm:mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span className="agreement-review-page__legal-copy-full">
                By clicking Agree and sign, you agree to be legally bound by
                this agreement.
              </span>
              <span className="agreement-review-page__legal-copy-compact">
                By signing, you accept this agreement.
              </span>
            </div>

            <div className="agreement-review-page__actions flex sm:flex-row sm:items-center sm:flex-wrap">
              <button
                type="button"
                onClick={() => navigate("/")}
                disabled={isSubmitting}
                className="agreement-review-page__back-button hidden h-[46px] w-full rounded-lg border border-gray-200 px-6 text-[15px] font-semibold text-[#120c80] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:block sm:w-auto"
              >
                Back to details
              </button>
              <button
                type="button"
                disabled={!canSign || isSubmitting}
                onClick={submitAgreement}
                className={`agreement-review-page__submit-button h-[46px] w-full rounded-lg px-8 text-[15px] font-semibold transition-all sm:w-auto ${
                  canSign && !isSubmitting
                    ? "bg-[#120c80] text-white hover:bg-[#0e0a66] shadow-md cursor-pointer"
                    : "bg-gray-100 text-gray-300 cursor-not-allowed shadow-none"
                }`}
              >
                {submissionPhase === "creating"
                  ? "Creating\u2026"
                  : submissionPhase === "configuring"
                    ? "Configuring\u2026"
                    : submissionPhase === "signing"
                      ? "Signing\u2026"
                      : "Agree and sign"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
